import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerLinks = [
    {
      title: 'Product',
      links: [
        { name: 'Features', href: '#features' },
        { name: 'Download', href: '#download' },
        { name: 'Pricing', href: '#pricing' },
        { name: 'Updates', href: '#updates' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Help Center', href: '#help' },
        { name: 'Contact Us', href: '#contact' },
        { name: 'Bug Report', href: '#bugs' },
        { name: 'Feature Request', href: '#request' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '#privacy' },
        { name: 'Terms of Service', href: '#terms' },
        { name: 'Cookie Policy', href: '#cookies' },
        { name: 'DMCA', href: '#dmca' }
      ]
    }
  ];

  const socialLinks = [
    { name: 'Twitter', icon: 'Twitter', href: '#twitter' },
    { name: 'GitHub', icon: 'Github', href: 'https://mugishaleobeni.netlify.app' },
    { name: 'Discord', icon: 'MessageCircle', href: '#discord' },
    { name: 'Email', icon: 'Mail', href: 'mailto:mugishaleobeni0@gmail.com' }
  ];

  return (
    <footer className="relative mt-20">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
      <div className="relative glass-container">
        <div className="glass-card p-8 lg:p-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="glass-card p-2">
                  <Icon name="Download" size={24} className="text-accent" />
                </div>
                <div>
                  <h3 className="text-fluid-lg font-bold text-foreground">Zee Downloader</h3>
                  <p className="text-xs text-muted-foreground font-mono">v2.1.0</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                The fastest and most reliable YouTube video downloader with glassmorphism design. 
                Download your favorite videos in high quality with just a few clicks.
              </p>
              
              {/* Social Links */}
              <div className="flex items-center gap-2">
                {socialLinks?.map((social) => (
                  <Button
                    key={social?.name}
                    variant="ghost"
                    size="icon"
                    iconName={social?.icon}
                    className="micro-lift glass-hover"
                    onClick={() => window.open(social?.href, '_blank')}
                  />
                ))}
              </div>
            </div>

            {/* Footer Links */}
            {footerLinks?.map((section) => (
              <div key={section?.title}>
                <h4 className="text-sm font-semibold text-foreground mb-4">
                  {section?.title}
                </h4>
                <ul className="space-y-2">
                  {section?.links?.map((link) => (
                    <li key={link?.name}>
                      <a
                        href={link?.href}
                        className="text-sm text-muted-foreground hover:text-foreground smooth-transition micro-lift inline-block"
                      >
                        {link?.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter Section */}
          <div className="border-t border-border/20 pt-8 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex-1">
                <h4 className="text-fluid-lg font-semibold text-foreground mb-2">
                  Stay Updated
                </h4>
                <p className="text-sm text-muted-foreground">
                  Get notified about new features, updates, and improvements to Zee Downloader.
                </p>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 md:w-64 px-4 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring smooth-transition"
                />
                <Button variant="default" iconName="Send" className="shrink-0">
                  Subscribe
                </Button>
              </div>
            </div>
          </div>

          {/* Security & Trust Badges */}
          <div className="border-t border-border/20 pt-8 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Shield" size={16} className="text-success" />
                  <span>SSL Secured</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Lock" size={16} className="text-success" />
                  <span>Privacy Protected</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Zap" size={16} className="text-accent" />
                  <span>Lightning Fast</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>Trusted by 100K+ users</span>
                <div className="flex items-center gap-1">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={12} className="text-warning fill-current" />
                  ))}
                  <span className="ml-1">4.9/5</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-border/20 pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-4">
                <p>© {currentYear} Zee Downloader. All rights reserved.</p>
                <div className="hidden md:flex items-center gap-1">
                  <span>Made with</span>
                  <Icon name="Heart" size={14} className="text-error fill-current" />
                  <span>for creators</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Icon name="Globe" size={14} />
                  English (US)
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} />
                  Worldwide
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;